import type React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DOMPurify from 'dompurify';

const FIELD_STACK_SPACING = 1;
const MIN_EMAIL_BODY_CONTENT_HEIGHT_PX = 200;
const MAX_EMAIL_BODY_CONTENT_HEIGHT_PX = 400;

type RootProps = { children: React.ReactNode };
const Root = ({ children }: RootProps) => <Stack spacing={FIELD_STACK_SPACING}>{children}</Stack>;

type HeaderProps = { hasError: boolean; headerLabel: React.ReactNode };
const Header = ({ hasError, headerLabel }: HeaderProps) => (
  <Typography variant="body2" color={hasError ? 'error' : 'text.secondary'}>
    {headerLabel}
  </Typography>
);

type EditorRootProps = { children: React.ReactNode };
const EditorRoot = ({ children }: EditorRootProps) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);

type ToolbarStripProps = { children: React.ReactNode };
const ToolbarStrip = ({ children }: ToolbarStripProps) => (
  <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'background.default' }}>{children}</Box>
);

type BodyProps = { children: React.ReactNode };
const Body = ({ children }: BodyProps) => (
  <Box
    sx={{
      maxHeight: MAX_EMAIL_BODY_CONTENT_HEIGHT_PX,
      minHeight: MIN_EMAIL_BODY_CONTENT_HEIGHT_PX,
      overflow: 'auto',
      p: 2,
      '& .tiptap': {
        outline: 'none',
        minHeight: MIN_EMAIL_BODY_CONTENT_HEIGHT_PX,
        '& p.is-editor-empty:first-of-type::before': {
          color: 'text.disabled',
          content: 'attr(data-placeholder)',
          float: 'left',
          height: 0,
          pointerEvents: 'none',
        },
      },
    }}
  >
    {children}
  </Box>
);

type ReadOnlyHtmlProps = { html: string };
const ReadOnlyHtml = ({ html }: ReadOnlyHtmlProps) => (
  <Typography
    variant="body2"
    component="div"
    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    sx={{
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      minHeight: MIN_EMAIL_BODY_CONTENT_HEIGHT_PX,
      color: 'text.disabled',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      p: 1,
    }}
  />
);

type ErrorCaptionProps = { message?: string };
const ErrorCaption = ({ message }: ErrorCaptionProps) =>
  message && (
    <Typography variant="caption" color="error">
      {message}
    </Typography>
  );

export const EmailTemplateRichTextEditorLayout = {
  Body,
  EditorRoot,
  ErrorCaption,
  Root,
  Header,
  ReadOnlyHtml,
  ToolbarStrip,
};
